import {
    pushCheckoutOptionDataToDataLayer,
    pushOrderConfirmationDataToDataLayer,
    viewProductsFromCart,
} from "@NA/Overrides/Common/Utilities/CustomTracking";
import { addToEndOfChain, replaceInChain } from "@insite/client-framework/HandlerCreator";
import {
    createHandlerChainRunnerOptionalParameter,
    Handler,
    HasOnSuccess,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { CartResult, getCart, GetCartApiParameter, updateCart } from "@insite/client-framework/Services/CartService";
import { chain, PopulateApiParameter } from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { updateCartForIterable } from "@NA/Services/ADIAbandonedCartV2Service";
import { getIfCartIsModifiied, removeCartModifiedFromLocalStorage } from "@NA/Utils/Utility";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";

type HandlerType = Handler<
    {
        shouldLoadFullCart?: boolean;
        replaceCart?: boolean;
        getPromotions?: boolean;
    } & HasOnSuccess,
    {
        apiParameter: GetCartApiParameter;
        apiResult: CartResult;
        needFullCart: boolean;
    }
>;

export const PopulateApiParameterCustom: HandlerType = props => {
    const state = props.getState();
    const cart = getCartState(state, API_URL_CURRENT_FRAGMENT);
    const isAuthenticated = state.context.session.isAuthenticated;
    const pageName = getCurrentPage(props.getState()).name;
    const pageType = getCurrentPage(props.getState()).type;
    const settings = getSettingsCollection(state);
    if (pageType === "CartPage") {
        props.apiParameter = {
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: isAuthenticated ? ["cartLines", "validation"] : ["validation"],
        };
    } else if (pageType === "OrderDetailsPage") {
        props.apiParameter = {
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: isAuthenticated ? ["cartLines", "paymentOptions"] : ["validation"],
        };
    } else {
        props.apiParameter = {
            cartId: API_URL_CURRENT_FRAGMENT,
            expand: isAuthenticated ? ["cartLines", "validation", "paymentOptions"] : ["validation"],
        };
    }

    if (props.needFullCart && pageType !== "CartPage") {
        props.apiParameter.forceRecalculation = true;
        props.apiParameter.allowInvalidAddress = true;
        props.apiParameter.expand = [
            ...(props.apiParameter.expand || []),
            "restrictions",
            "tax",
            "carriers",
            "paymentOptions",
            "costCodes",
            "shipping",
            "shipTos",
        ];
    }

    if (pageType === "CartPage" && cart.value !== undefined && cart.value.cartLines?.length === 0) {
        props.apiParameter.expand = [...(props.apiParameter.expand || []), "costCodes"];
    } else if (pageType === "CartPage") {
        props.apiParameter.expand = ["cartLines", "costCodes"];
        if (settings?.cartSettings?.showTaxAndShipping) {
            props.apiParameter.expand = [...props.apiParameter.expand, "shipping"];
        }
    }
};

replaceInChain(chain, PopulateApiParameter, PopulateApiParameterCustom);

export const updateCartLocalValueLocal = (props: any) => {
    const { getState } = props;
    const { context } = getState();
    const isAuthenticated = context.session.isAuthenticated;
    const currentCartValue = getCurrentCartState(getState()).value?.orderGrandTotal.toFixed(0) || '0';
    localStorage.setItem('grandTotal', isAuthenticated ? currentCartValue : '0');    
    localStorage.removeItem('placeorder_clicked');
};

export const CallUpdateCartApi: HandlerType = (props: any) => {
    const { getState, apiResult } = props;
    const { context, pages } = getState();
    const { session, isUpdatingCart, settings } = context;
    const { isUpdatingCartLine } = pages.cart;
    const isAuthenticated = session.isAuthenticated;
    const isIterableEnabled = settings.settingsCollection?.adiIterableSettingSettings?.isIterableEnabled;

    const currentCartValue = getCurrentCartState(getState()).value?.orderGrandTotal.toFixed(0) || "0";
    const previousCartValue = localStorage.getItem("grandTotal");
    const placeOrderEvent = localStorage.getItem("placeorder_clicked");

    if (
        (isUpdatingCart ||
            isUpdatingCartLine ||
            (currentCartValue !== previousCartValue && placeOrderEvent !== "true")) &&
        isAuthenticated &&
        isIterableEnabled
    ) {
        updateCartForIterable(apiResult.cart);
        removeCartModifiedFromLocalStorage();
        updateCartLocalValueLocal(props);
    }
};

addToEndOfChain(chain, CallUpdateCartApi);

export const ViewProductsFromCartDataLayer: HandlerType = props => {
    const listName = sessionStorage.getItem("is_from_cart_page") ?? "";
    sessionStorage.setItem("is_from_cart_page", "");
    if (listName === "true") {
        const cartLines: CartLineModel[] = props.apiResult.cart.cartLines ?? [];
        viewProductsFromCart(cartLines, props.getState().context.session);
    }
};

addToEndOfChain(chain, ViewProductsFromCartDataLayer);
